import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { marketService, organizationService } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { CalendarIcon, Scissors, Clock, X, Plus, Search, Users, Globe, Lock, UserCheck, Image as ImageIcon, Loader2, Coins } from 'lucide-react';
import { cn } from '../lib/utils';
import ImageCropperModal from '../components/ImageCropperModal';
import { Switch } from '../components/ui/switch';

const CreateMarket = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [title, setTitle] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [description, setDescription] = useState('');
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState('23:59');
  const [loading, setLoading] = useState(false);
  const [minBetAmount, setMinBetAmount] = useState('1.00');
  
  // Tiered markets state
  const [isTiered, setIsTiered] = useState(false);
  const [thresholdMode, setThresholdMode] = useState('range'); // 'range' | 'manual'
  const [startValue, setStartValue] = useState('');
  const [endValue, setEndValue] = useState('');
  const [stepSize, setStepSize] = useState('');
  const [manualThresholds, setManualThresholds] = useState('');
  
  const [staggerDeadlines, setStaggerDeadlines] = useState(false);
  const [staggerInterval, setStaggerInterval] = useState('1_month'); // '1_hour' | '1_day' | '1_week' | '1_month' | '1_year' | 'custom'
  const [customDeadlines, setCustomDeadlines] = useState({}); // maps index -> Date

  const getThresholds = () => {
    if (thresholdMode === 'range') {
      const start = parseFloat(startValue);
      const end = parseFloat(endValue);
      const step = parseFloat(stepSize);
      if (isNaN(start) || isNaN(end) || isNaN(step) || step <= 0 || start >= end) {
        return [];
      }
      const count = Math.floor((end - start) / step) + 1;
      if (count <= 0 || count > 30) {
        return [];
      }
      const list = [];
      for (let val = start; val <= end; val += step) {
        list.push(val);
      }
      return list;
    } else {
      if (!manualThresholds) return [];
      const parsedValues = manualThresholds
        .split(',')
        .map(s => s.trim())
        .filter(s => s !== '');
      
      const allNumeric = parsedValues.every(val => !isNaN(parseFloat(val)) && isFinite(val));
      if (allNumeric) {
        return parsedValues.map(val => parseFloat(val)).sort((a, b) => a - b);
      } else {
        return parsedValues;
      }
    }
  };

  const getTiers = () => {
    const vals = getThresholds();
    if (!endDate || vals.length === 0) return [];
    
    const baseDateTime = new Date(endDate);
    const [hours, minutes] = endTime.split(':').map(Number);
    baseDateTime.setHours(hours, minutes, 0, 0);

    return vals.map((val, idx) => {
      let formattedVal = val;
      if (typeof val === 'number') {
        formattedVal = (Math.floor(val) === val) 
          ? new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', { maximumFractionDigits: 0 }).format(val)
          : new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
      }
      let tierTitle = title;
      if (tierTitle.includes('(WERT)')) {
        tierTitle = tierTitle.replace('(WERT)', formattedVal);
      } else if (tierTitle.includes('(VALUE)')) {
        tierTitle = tierTitle.replace('(VALUE)', formattedVal);
      } else {
        tierTitle = tierTitle ? `${tierTitle} (${formattedVal})` : `(${formattedVal})`;
      }

      let calculatedEndDate = new Date(baseDateTime);
      if (staggerDeadlines) {
        if (staggerInterval === '1_hour') {
          calculatedEndDate.setHours(baseDateTime.getHours() + idx);
        } else if (staggerInterval === '1_day') {
          calculatedEndDate.setDate(baseDateTime.getDate() + idx);
        } else if (staggerInterval === '1_week') {
          calculatedEndDate.setDate(baseDateTime.getDate() + idx * 7);
        } else if (staggerInterval === '1_month') {
          calculatedEndDate.setMonth(baseDateTime.getMonth() + idx);
        } else if (staggerInterval === '1_year') {
          calculatedEndDate.setFullYear(baseDateTime.getFullYear() + idx);
        }
      }

      const customDate = customDeadlines[idx];
      const finalEndDate = (staggerDeadlines && customDate) ? customDate : calculatedEndDate;

      return {
        value: val,
        title: tierTitle,
        endDate: finalEndDate,
      };
    });
  };

  const handleCustomDeadlineChange = (idx, newDate) => {
    setCustomDeadlines(prev => ({
      ...prev,
      [idx]: newDate
    }));
  };
  
  // New features
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(() => localStorage.getItem('last_market_category') || '');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  
  // Visibility
  const [visibilityType, setVisibilityType] = useState(() => localStorage.getItem('last_market_visibility_type') || 'public');
  const [emailDomain, setEmailDomain] = useState(() => localStorage.getItem('last_market_email_domain') || '');
  const [selectedUsers, setSelectedUsers] = useState(() => {
    try {
      const saved = localStorage.getItem('last_market_selected_users');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [selectedGroup, setSelectedGroup] = useState(() => localStorage.getItem('last_market_selected_group') || '');
  const [groups, setGroups] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  
  const handleMarketImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'de' ? 'Datei ist zu groß (max. 10MB)' : 'File is too large (max. 10MB)');
      return;
    }

    setSelectedFileForCrop(file);
    setIsCropperOpen(true);
    e.target.value = ''; // Reset so the same file can be uploaded again
  };

  const handleCroppedMarketImageUpload = async (croppedBlob) => {
    const localUrl = URL.createObjectURL(croppedBlob);
    setLocalPreviewUrl(localUrl);

    const formData = new FormData();
    formData.append('file', croppedBlob, 'market.jpg');

    setUploadingImage(true);
    try {
      const response = await apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setImageUrl(response.data.url);
      toast.success(language === 'de' ? 'Bild erfolgreich hochgeladen!' : 'Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Upload' : 'Upload failed'));
      setLocalPreviewUrl('');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // User tagging
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [tagSearchResults, setTagSearchResults] = useState([]);

  // Visibility specific search
  const [visSearch, setVisSearch] = useState('');
  const [visSearchResults, setVisSearchResults] = useState([]);

  const dateLocale = language === 'de' ? de : enUS;

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchCategories(), fetchGroups()]);
    };
    loadInitialData();
  }, []);

  // Organisationen des Nutzers laden (fuer die Org-Auswahl der Wette)
  useEffect(() => {
    organizationService.getAll()
      .then(res => {
        const mine = (res.data || []).filter(o => o.membership === 'member');
        setOrgs(mine);
        if (mine.length > 0) setSelectedOrg(prev => prev || mine[0].id);
      })
      .catch(() => {});
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(`/categories`);
      const cats = response.data;
      setCategories(cats);
      
      // Falls die gespeicherte Kategorie nicht mehr existiert (außer 'none'), zurücksetzen
      const savedCat = localStorage.getItem('last_market_category');
      if (savedCat && savedCat !== 'none' && !cats.find(c => c.name === savedCat)) {
        setSelectedCategory('');
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiClient.get(`/groups`);
      const groupsData = response.data;
      setGroups(groupsData);
      
      // Falls die gespeicherte Gruppe nicht mehr existiert, zurücksetzen
      const savedGroup = localStorage.getItem('last_market_selected_group');
      if (savedGroup && !groupsData.find(g => g.id === savedGroup)) {
        setSelectedGroup('');
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const searchUsersForTags = async (query) => {
    if (query.length < 2) {
      setTagSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get(`/users/search?q=${query}`);
      setTagSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users for tags:', error);
    }
  };

  const searchUsersForVisibility = async (query) => {
    if (query.length < 2) {
      setVisSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get(`/users/search?q=${query}`);
      setVisSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users for visibility:', error);
    }
  };

  const handleTagSearch = (value) => {
    setTagSearch(value);
    searchUsersForTags(value);
  };

  const handleVisSearch = (value) => {
    setVisSearch(value);
    searchUsersForVisibility(value);
  };

  const addTaggedUser = (user) => {
    if (!taggedUsers.find(u => u.id === user.id)) {
      setTaggedUsers([...taggedUsers, { id: user.id, username: user.username }]);
    }
    setTagSearch('');
    setTagSearchResults([]);
  };

  const removeTaggedUser = (userId) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  const addSelectedUser = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, { id: user.id, username: user.username }]);
    }
    setVisSearch('');
    setVisSearchResults([]);
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await apiClient.post(`/categories`, { name: newCategoryName });
      toast.success(t('categoryCreated'));
      fetchCategories();
      setSelectedCategory(newCategoryName);
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || title.length < 5) {
      toast.error(language === 'de' ? 'Titel muss mindestens 5 Zeichen haben' : 'Title must be at least 5 characters');
      return;
    }

    if (!endDate) {
      toast.error(language === 'de' ? 'Bitte wähle ein Enddatum' : 'Please select an end date');
      return;
    }

    const minBetFloat = parseFloat(minBetAmount);
    if (isNaN(minBetFloat) || minBetFloat < 1.00 || minBetFloat > 50.00) {
      toast.error(language === 'de' 
        ? 'Der Mindesteinsatz muss zwischen 1,00 € und 50,00 € liegen.' 
        : 'The minimum bet must be between 1.00 € and 50.00 €.');
      return;
    }

    const [hours, minutes] = endTime.split(':').map(Number);
    const endDateTime = new Date(endDate);
    endDateTime.setHours(hours, minutes, 0, 0);

    if (endDateTime <= new Date()) {
      // Wenn das Datum heute ist, erlauben wir es, solange die Zeit in der Zukunft liegt.
      // Die obige Bedingung 'endDateTime <= new Date()' prüft bereits auf Millisekunden-Ebene.
      // Ein Fehler könnte auftreten, wenn der Benutzer gerade erst die Zeit eingestellt hat.
      // Wir geben eine kleine Toleranz von 1 Minute.
      if (endDateTime <= new Date(Date.now() - 60000)) {
        toast.error(language === 'de' ? 'Endzeitpunkt muss in der Zukunft liegen' : 'End time must be in the future');
        return;
      }
    }

    // Build visibility settings
    // Tiered markets are always public to avoid empty allowed_users corruption
    const effectiveVisibilityType = isTiered ? 'public' : visibilityType;
    let visibility = { type: effectiveVisibilityType };
    if (effectiveVisibilityType === 'email_domain') {
      visibility.email_domain = emailDomain;
    } else if (effectiveVisibilityType === 'specific_users') {
      if (selectedUsers.length === 0) {
        toast.error(language === 'de' ? 'Bitte wähle mindestens einen Benutzer aus' : 'Please select at least one user');
        return;
      }
      visibility.allowed_users = selectedUsers.map(u => u.id);
    } else if (effectiveVisibilityType === 'group') {
      visibility.group_id = selectedGroup;
    }
    let thresholds = [];
    if (isTiered) {
      if (thresholdMode === 'range') {
        const start = parseFloat(startValue);
        const end = parseFloat(endValue);
        const step = parseFloat(stepSize);
        if (isNaN(start) || isNaN(end) || isNaN(step) || step <= 0 || start >= end) {
          toast.error(language === 'de' ? 'Bitte gib gültige Bereichswerte ein.' : 'Please enter valid range values.');
          return;
        }
        const count = Math.floor((end - start) / step) + 1;
        if (count <= 0) {
          toast.error(language === 'de' ? 'Keine Schwellenwerte generiert.' : 'No thresholds generated.');
          return;
        }
        if (count > 30) {
          toast.error(language === 'de' ? 'Maximal 30 Schwellenwerte sind erlaubt.' : 'A maximum of 30 thresholds is allowed.');
          return;
        }
        for (let val = start; val <= end; val += step) {
          thresholds.push(val);
        }
      } else {
        const parsedValues = manualThresholds
          .split(',')
          .map(s => s.trim())
          .filter(s => s !== '');
        
        if (parsedValues.length === 0) {
          toast.error(t('invalidThresholds'));
          return;
        }
        if (parsedValues.length > 30) {
          toast.error(language === 'de' ? 'Maximal 30 Schwellenwerte sind erlaubt.' : 'A maximum of 30 thresholds is allowed.');
          return;
        }
        
        const allNumeric = parsedValues.every(val => !isNaN(parseFloat(val)) && isFinite(val));
        if (allNumeric) {
          thresholds = parsedValues.map(val => parseFloat(val)).sort((a, b) => a - b);
        } else {
          thresholds = parsedValues;
        }
      }
    }

    const getTierEndDate = (idx) => {
      if (staggerDeadlines) {
        if (customDeadlines[idx]) {
          return customDeadlines[idx];
        }
        const calculatedEndDate = new Date(endDateTime);
        if (staggerInterval === '1_hour') {
          calculatedEndDate.setHours(endDateTime.getHours() + idx);
        } else if (staggerInterval === '1_day') {
          calculatedEndDate.setDate(endDateTime.getDate() + idx);
        } else if (staggerInterval === '1_week') {
          calculatedEndDate.setDate(endDateTime.getDate() + idx * 7);
        } else if (staggerInterval === '1_month') {
          calculatedEndDate.setMonth(endDateTime.getMonth() + idx);
        } else if (staggerInterval === '1_year') {
          calculatedEndDate.setFullYear(endDateTime.getFullYear() + idx);
        }
        return calculatedEndDate;
      }
      return endDateTime;
    };

    const tieredThresholdsPayload = thresholds.map((val, idx) => ({
      value: val,
      end_date: getTierEndDate(idx).toISOString(),
    }));

    setLoading(true);
    try {
      const response = await marketService.create({
        title,
        description,
        end_date: endDateTime.toISOString(),
        category: selectedCategory === 'none' ? null : selectedCategory || null,
        tagged_users: taggedUsers.map(u => u.username),
        visibility,
        image_url: imageUrl,
        group_id: effectiveVisibilityType === 'group' ? selectedGroup : null,
        is_tiered: isTiered,
        tiered_title_template: title,
        tiered_thresholds: tieredThresholdsPayload,
        min_bet_amount: minBetFloat,
        organization_id: selectedOrg || null,
      });
      // Save preferences to localStorage
      localStorage.setItem('last_market_category', selectedCategory);
      localStorage.setItem('last_market_visibility_type', visibilityType);
      localStorage.setItem('last_market_email_domain', emailDomain);
      localStorage.setItem('last_market_selected_users', JSON.stringify(selectedUsers));
      localStorage.setItem('last_market_selected_group', selectedGroup);

      toast.success(t('marketCreated'));
      
      // Delay navigation slightly to ensure the backend has processed everything
      // and provide a smoother transition with the toast
      setTimeout(() => {
        navigate(`/market/${response.data.id}`);
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('marketCreationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="create-market-page">

      <main className="max-w-2xl mx-auto px-4 py-4 sm:py-8">
        <Card className="border-border/50">
          <CardHeader className="text-center p-4 sm:p-6">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-red-600/20">
                <Scissors className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl">{t('createAMarket')}</CardTitle>
            <CardDescription className="text-sm">{t('createYesNo')}</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
              {/* Organisation (nur relevant, wenn Nutzer in mehreren ist) */}
              {orgs.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-sm font-bold">{language === 'de' ? 'Organisation' : 'Organization'}</Label>
                  <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                    <SelectTrigger><SelectValue placeholder={language === 'de' ? 'Organisation wählen' : 'Choose organization'} /></SelectTrigger>
                    <SelectContent>
                      {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {language === 'de' ? 'In welcher Organisation soll die Wette erscheinen?' : 'Which organization should see this market?'}
                  </p>
                </div>
              )}

              {/* Staffelungswette Toggle */}
              <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-xl border border-border/50">
                <div className="space-y-0.5 pr-2">
                  <Label htmlFor="is-tiered" className="text-sm font-bold cursor-pointer">{t('isTieredBet')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'de' 
                      ? 'Erstellt mehrere Wetten für verschiedene Schwellenwerte (z.B. Bitcoin-Preise)' 
                      : 'Creates multiple markets for different threshold values (e.g. Bitcoin prices)'}
                  </p>
                </div>
                <Switch
                  id="is-tiered"
                  checked={isTiered}
                  onCheckedChange={setIsTiered}
                />
              </div>

              {/* Tiered Options */}
              {isTiered && (
                <div className="p-4 bg-secondary/10 border border-border/50 rounded-xl space-y-4 animate-in slide-in-from-top-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{t('tieredOptions')}</h3>
                  
                  {/* Threshold Mode Selector */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold">{t('thresholdMode')}</Label>
                    <Select value={thresholdMode} onValueChange={setThresholdMode}>
                      <SelectTrigger className="h-10 bg-card">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="range">{t('thresholdModeRange')}</SelectItem>
                        <SelectItem value="manual">{t('thresholdModeManual')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {thresholdMode === 'range' ? (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="start-val" className="text-[10px] uppercase font-bold text-muted-foreground">{t('startValue')}</Label>
                        <Input
                          id="start-val"
                          type="number"
                          placeholder="2000"
                          value={startValue}
                          onChange={(e) => setStartValue(e.target.value)}
                          className="h-10 bg-card"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="end-val" className="text-[10px] uppercase font-bold text-muted-foreground">{t('endValue')}</Label>
                        <Input
                          id="end-val"
                          type="number"
                          placeholder="50000"
                          value={endValue}
                          onChange={(e) => setEndValue(e.target.value)}
                          className="h-10 bg-card"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="step-val" className="text-[10px] uppercase font-bold text-muted-foreground">{t('stepSize')}</Label>
                        <Input
                          id="step-val"
                          type="number"
                          placeholder="5000"
                          value={stepSize}
                          onChange={(e) => setStepSize(e.target.value)}
                          className="h-10 bg-card"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="manual-vals" className="text-xs font-bold">{t('manualThresholds')}</Label>
                      <Textarea
                        id="manual-vals"
                        placeholder={t('manualThresholdsPlaceholder')}
                        value={manualThresholds}
                        onChange={(e) => setManualThresholds(e.target.value)}
                        rows={2}
                        className="min-h-[60px] bg-card"
                      />
                    </div>
                  )}

                  {/* Staggered Deadlines Toggle */}
                  <div className="flex items-center justify-between p-3 bg-secondary/15 rounded-xl border border-border/40 mt-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="stagger-deadlines" className="text-xs font-bold cursor-pointer">{t('staggerDeadlines')}</Label>
                      <p className="text-[10px] text-muted-foreground">
                        {language === 'de' 
                          ? 'Vergibt für jede Stufe ein unterschiedliches Enddatum' 
                          : 'Assigns a different end date to each tier'}
                      </p>
                    </div>
                    <Switch
                      id="stagger-deadlines"
                      checked={staggerDeadlines}
                      onCheckedChange={setStaggerDeadlines}
                    />
                  </div>

                  {staggerDeadlines && (
                    <div className="space-y-3 pt-2 animate-in fade-in duration-200">
                      <div className="space-y-1">
                        <Label className="text-xs font-bold">{t('staggerInterval')}</Label>
                        <Select value={staggerInterval} onValueChange={(val) => {
                          setStaggerInterval(val);
                          setCustomDeadlines({}); // Reset custom deadlines when interval changes
                        }}>
                          <SelectTrigger className="h-9 bg-card">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1_hour">{t('hourStagger')}</SelectItem>
                            <SelectItem value="1_day">{t('dayStagger')}</SelectItem>
                            <SelectItem value="1_week">{t('weekStagger')}</SelectItem>
                            <SelectItem value="1_month">{t('monthStagger')}</SelectItem>
                            <SelectItem value="1_year">{t('yearStagger')}</SelectItem>
                            <SelectItem value="custom">{t('customStagger')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Live Preview of Tiers */}
                  {(() => {
                    const tiers = getTiers();
                    if (tiers.length === 0) return null;
                    return (
                      <div className="space-y-2 mt-4 pt-2 border-t border-border/30">
                        <Label className="text-xs font-bold">{t('tiersPreview')}</Label>
                        <div className="max-h-60 overflow-y-auto border border-border/50 rounded-xl divide-y divide-border/30 bg-card/50">
                          {tiers.map((tier, idx) => (
                            <div key={idx} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
                              <div className="space-y-0.5 max-w-sm">
                                <span className="font-bold text-muted-foreground mr-1">#{idx + 1}</span>
                                <span className="font-semibold text-foreground">{tier.title}</span>
                              </div>
                              
                              <div className="flex items-center gap-2 shrink-0">
                                {staggerDeadlines ? (
                                  <div className="flex items-center gap-1.5 bg-secondary/30 px-2 py-1 rounded-lg border border-border/40">
                                    <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span className="font-mono text-[11px]">
                                      {format(tier.endDate, 'dd.MM.yyyy HH:mm')}
                                    </span>
                                    
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-5 w-5 p-0 hover:bg-secondary">
                                          <Clock className="w-3 h-3 text-primary" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-3 space-y-3" align="end">
                                        <div className="space-y-1">
                                          <Label className="text-[10px] font-bold uppercase text-muted-foreground">Datum / Date</Label>
                                          <Calendar
                                            mode="single"
                                            selected={tier.endDate}
                                            onSelect={(date) => {
                                              if (!date) return;
                                              const currentDateTime = new Date(tier.endDate);
                                              date.setHours(currentDateTime.getHours(), currentDateTime.getMinutes(), 0, 0);
                                              handleCustomDeadlineChange(idx, date);
                                            }}
                                            locale={dateLocale}
                                            initialFocus
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-[10px] font-bold uppercase text-muted-foreground">Uhrzeit / Time</Label>
                                          <Input
                                            type="time"
                                            value={format(tier.endDate, 'HH:mm')}
                                            onChange={(e) => {
                                              const [h, m] = e.target.value.split(':').map(Number);
                                              const newDate = new Date(tier.endDate);
                                              newDate.setHours(h, m, 0, 0);
                                              handleCustomDeadlineChange(idx, newDate);
                                            }}
                                            className="h-8 text-xs bg-card"
                                          />
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground italic">
                                    {t('sharedDeadline')}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-bold">
                  {isTiered ? t('titleTemplate') : t('questionTitle')}
                </Label>
                <Input
                  id="title"
                  placeholder={isTiered ? t('titleTemplatePlaceholder') : t('questionPlaceholder')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  required
                  className="h-11 sm:h-12"
                  data-testid="market-title-input"
                />
                {isTiered && (
                  <p className="text-[10px] text-muted-foreground italic">
                    {language === 'de' 
                      ? 'Wichtig: Der Titel MUSS (WERT) enthalten (z.B. "Wird der Bitcoin über (WERT) € sein?").' 
                      : 'Important: The title MUST contain (VALUE) (e.g., "Will Bitcoin be above (VALUE) €?").'}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground text-right">{title.length}/200</p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-bold">{t('descriptionOptional')}</Label>
                <Textarea
                  id="description"
                  placeholder={t('descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  className="min-h-[100px]"
                  data-testid="market-description-input"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label className="text-sm font-bold">{t('category')}</Label>
                <div className="flex gap-2">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="flex-1 h-11">
                      <SelectValue placeholder={t('noCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('noCategory')}</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => setShowNewCategory(!showNewCategory)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {showNewCategory && (
                  <div className="flex gap-2 mt-2 animate-in slide-in-from-top-1">
                    <Input
                      placeholder={t('categoryName')}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="h-11"
                    />
                    <Button type="button" className="h-11" onClick={createCategory}>{t('create')}</Button>
                  </div>
                )}
              </div>

              {/* Minimum Bet Amount */}
              <div className="space-y-2">
                <Label htmlFor="minBetAmount" className="text-sm font-bold flex items-center gap-2">
                  <Coins className="w-4 h-4 text-yellow-500" />
                  {t('minBetAmount')}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">€</span>
                  <Input
                    id="minBetAmount"
                    type="number"
                    step="0.01"
                    min="1.00"
                    max="50.00"
                    placeholder={t('minBetAmountPlaceholder')}
                    value={minBetAmount}
                    onChange={(e) => setMinBetAmount(e.target.value)}
                    className="pl-8 h-11"
                    data-testid="min-bet-amount-input"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {t('minBetAmountHint')}
                </p>
              </div>

              {/* Bild URL */}
              <div className="space-y-2">
                <Label htmlFor="imageUrl" className="text-sm font-bold flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  {language === 'de' ? 'Wetten-Bild' : 'Market Image'}
                </Label>
                
                <div className="flex flex-col gap-4">
                  {imageUrl || localPreviewUrl ? (
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border bg-muted">
                      <img 
                        src={localPreviewUrl || imageUrl} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                      {uploadingImage && (
                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white z-10">
                          <Loader2 className="w-8 h-8 animate-spin mb-2" />
                          <span className="text-xs font-semibold">
                            {language === 'de' ? 'Wird hochgeladen...' : 'Uploading...'}
                          </span>
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 z-20"
                        onClick={() => {
                          setImageUrl('');
                          setLocalPreviewUrl('');
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/20 rounded-xl p-6 bg-secondary/10 hover:bg-secondary/20 transition-colors cursor-pointer relative group">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleMarketImageUpload} 
                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        disabled={uploadingImage}
                      />
                      <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground group-hover:scale-110 transition-transform" />
                      <p className="text-sm font-medium">
                        {language === 'de' ? 'Bild hochladen' : 'Upload Image'}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {language === 'de' ? 'oder unten eine Bild-URL eingeben' : 'or enter an image URL below'}
                      </p>
                    </div>
                  )}
                  
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="imageUrl"
                      placeholder={language === 'de' ? "https://example.com/bild.jpg" : "https://example.com/image.jpg"}
                      value={imageUrl}
                      onChange={(e) => {
                        setImageUrl(e.target.value);
                        setLocalPreviewUrl(''); // Clear local preview if manually changed
                      }}
                      className="pl-10 h-11"
                    />
                  </div>
                  
                  <p className="text-[10px] text-muted-foreground">
                    {language === 'de' 
                      ? 'Ein Bild macht deine Wette attraktiver. Empfohlen: 16:9 Format.' 
                      : 'An image makes your bet more attractive. Recommended: 16:9 format.'}
                  </p>
                </div>
              </div>

              {/* User Tagging */}
              <div className="space-y-2">
                <Label className="text-sm font-bold">{t('tagUsers')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('searchUsers')}
                    value={tagSearch}
                    onChange={(e) => handleTagSearch(e.target.value)}
                    className="pl-10 h-11"
                  />
                </div>
                {tagSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full max-w-md bg-card border border-border rounded-lg shadow-lg mt-1">
                    {tagSearchResults.slice(0, 5).map(user => (
                      <div
                        key={user.id}
                        className="px-3 py-2 hover:bg-secondary cursor-pointer"
                        onClick={() => addTaggedUser(user)}
                      >
                        @{user.username}
                      </div>
                    ))}
                  </div>
                )}
                {taggedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {taggedUsers.map(user => (
                      <Badge key={user.id} variant="secondary" className="gap-1">
                        @{user.username}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeTaggedUser(user.id)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div className="space-y-3">
                <Label className="text-sm font-bold">{t('visibility')}</Label>
                <Select value={visibilityType} onValueChange={setVisibilityType}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        {t('visibilityPublic')}
                      </div>
                    </SelectItem>
                    <SelectItem value="email_domain">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        {t('visibilityEmailDomain')}
                      </div>
                    </SelectItem>
                    <SelectItem value="specific_users">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        {t('visibilitySpecificUsers')}
                      </div>
                    </SelectItem>
                    <SelectItem value="group">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {t('visibilityGroup')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {visibilityType === 'email_domain' && (
                  <Input
                    placeholder={t('emailDomain')}
                    value={emailDomain}
                    onChange={(e) => setEmailDomain(e.target.value)}
                    className="h-11"
                  />
                )}

                {visibilityType === 'specific_users' && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t('selectUsers')}
                        value={visSearch}
                        onChange={(e) => handleVisSearch(e.target.value)}
                        className="pl-10 h-11"
                      />
                    </div>
                    {visSearchResults.length > 0 && visibilityType === 'specific_users' && (
                      <div className="bg-card border border-border rounded-lg shadow-lg">
                        {visSearchResults.slice(0, 5).map(user => (
                          <div
                            key={user.id}
                            className="px-3 py-2 hover:bg-secondary cursor-pointer"
                            onClick={() => addSelectedUser(user)}
                          >
                            {user.username}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map(user => (
                          <Badge key={user.id} variant="secondary" className="gap-1">
                            {user.username}
                            <X className="w-3 h-3 cursor-pointer" onClick={() => removeSelectedUser(user.id)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {visibilityType === 'group' && (
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t('selectGroup')} />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map(group => (
                        <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* End Date & Time */}
              <div className="space-y-2">
                <Label className="text-sm font-bold">{t('endDateTime')}</Label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("flex-1 h-11 justify-start text-left font-normal", !endDate && "text-muted-foreground")}
                        data-testid="market-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP", { locale: dateLocale }) : t('pickDateTime')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        locale={dateLocale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="relative w-full sm:w-32">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="pl-10 h-11"
                      data-testid="market-time-picker"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 h-12 text-base font-bold shadow-lg shadow-red-600/10"
                disabled={loading}
                data-testid="create-market-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('creating')}
                  </>
                ) : (
                  t('createMarket')
                )}
              </Button>
            </CardContent>
          </form>
        </Card>

        <ImageCropperModal
          isOpen={isCropperOpen}
          onClose={() => setIsCropperOpen(false)}
          imageFile={selectedFileForCrop}
          circular={false}
          aspectRatio={16 / 9}
          onCrop={handleCroppedMarketImageUpload}
          language={language}
        />
      </main>
    </div>
  );
};

export default CreateMarket;
